/**
 * Types de dépense pour qualification factures d'achat.
 * L'utilisateur choisit une catégorie métier simple ; le système déduit TVA et compte.
 */

export type TypeDepense =
  | "marchandises"
  | "prestations"
  | "loyer"
  | "telecom_internet"
  | "transport_carburant"
  | "eau_electricite_gaz"
  | "frais_bancaires"
  | "fournitures"
  | "entretien_reparations"
  | "marketing_publicite"
  | "assurances"
  | "impots_taxes"
  | "immobilisation"
  | "autre";

export type SousTypePrestation =
  | "conseil"
  | "developpement_it"
  | "marketing"
  | "juridique"
  | "comptabilite"
  | "autre";

export type RegimeTva =
  | "biens"        // TVA biens
  | "services"     // TVA services
  | "non_applicable"; // 0 ou exonéré

export interface TypeDepenseConfig {
  id: TypeDepense;
  label: string;
  regime_tva: RegimeTva;
  effet_comptable: string;
  compte_propose: string;
  traitement_propose: string;
  sousTypes?: { id: SousTypePrestation; label: string }[];
}

export const TYPES_DEPENSE: TypeDepenseConfig[] = [
  { id: "marchandises", label: "Marchandises", regime_tva: "biens", effet_comptable: "dette fournisseur à constater", compte_propose: "60xx", traitement_propose: "Achat de marchandises" },
  { id: "prestations", label: "Prestations", regime_tva: "services", effet_comptable: "dette fournisseur à constater", compte_propose: "62xx", traitement_propose: "Prestation de services", sousTypes: [
    { id: "conseil", label: "Conseil" },
    { id: "developpement_it", label: "Développement / IT" },
    { id: "marketing", label: "Marketing" },
    { id: "juridique", label: "Juridique" },
    { id: "comptabilite", label: "Comptabilité" },
    { id: "autre", label: "Autre" },
  ]},
  { id: "loyer", label: "Loyer", regime_tva: "services", effet_comptable: "dette fournisseur à constater", compte_propose: "613", traitement_propose: "Loyer et charges locatives" },
  { id: "telecom_internet", label: "Télécom & Internet", regime_tva: "services", effet_comptable: "dette fournisseur à constater", compte_propose: "626", traitement_propose: "Télécommunications" },
  { id: "transport_carburant", label: "Transport & Carburant", regime_tva: "biens", effet_comptable: "dette fournisseur à constater", compte_propose: "625", traitement_propose: "Déplacements et transports" },
  { id: "eau_electricite_gaz", label: "Eau / Électricité / Gaz", regime_tva: "biens", effet_comptable: "dette fournisseur à constater", compte_propose: "622", traitement_propose: "Énergie et eau" },
  { id: "frais_bancaires", label: "Frais bancaires", regime_tva: "non_applicable", effet_comptable: "charge à constater", compte_propose: "631", traitement_propose: "Frais bancaires" },
  { id: "fournitures", label: "Fournitures", regime_tva: "biens", effet_comptable: "dette fournisseur à constater", compte_propose: "604", traitement_propose: "Fournitures consommables" },
  { id: "entretien_reparations", label: "Entretien & Réparations", regime_tva: "services", effet_comptable: "dette fournisseur à constater", compte_propose: "615", traitement_propose: "Entretiens et réparations" },
  { id: "marketing_publicite", label: "Marketing & Publicité", regime_tva: "services", effet_comptable: "dette fournisseur à constater", compte_propose: "623", traitement_propose: "Publicité et relations publiques" },
  { id: "assurances", label: "Assurances", regime_tva: "services", effet_comptable: "dette fournisseur à constater", compte_propose: "616", traitement_propose: "Primes d'assurance" },
  { id: "impots_taxes", label: "Impôts & Taxes", regime_tva: "non_applicable", effet_comptable: "dette État à constater", compte_propose: "44x", traitement_propose: "Impôts et taxes" },
  { id: "immobilisation", label: "Immobilisation", regime_tva: "biens", effet_comptable: "immobilisation à constater", compte_propose: "2xxx", traitement_propose: "Acquisition immobilisation" },
  { id: "autre", label: "Autre", regime_tva: "services", effet_comptable: "dette fournisseur à constater", compte_propose: "658", traitement_propose: "Charges diverses" },
];

export function getTypeDepenseConfig(id: TypeDepense): TypeDepenseConfig | undefined {
  return TYPES_DEPENSE.find((t) => t.id === id);
}

export function getLabelRegimeTva(regime: RegimeTva): string {
  switch (regime) {
    case "biens": return "Biens";
    case "services": return "Services";
    case "non_applicable": return "Non applicable";
    default: return "—";
  }
}

export interface MetadataQualificationFactureAchat {
  type_depense: TypeDepense;
  sous_type_depense?: SousTypePrestation | null;
  regime_tva: RegimeTva;
  traitement_comptable_source: "ui_facture_achat";
}
